import {useQuery} from '@tanstack/react-query'
import {useCallback, useEffect} from 'react'
import {PermissionService} from '../service/permission.services'
import {EResource, EPermission} from '../types/permission.type'
import useAppStore from '../store'

export function useClientPermission(clientId: string | undefined) {
  const clientPermissions = useAppStore(state =>
    clientId ? state.clientPermissions[clientId] : undefined,
  )
  const setClientPermissions = useAppStore(state => state.setClientPermissions)

  const {data, isLoading, error} = useQuery({
    queryKey: ['clientPermissions', clientId],
    queryFn: () => PermissionService.getPermissionsForClient(clientId!),
    enabled: !!clientId,
    staleTime: Infinity, // Permissions are valid indefinitely
    gcTime: 60 * 60 * 1000, // Keep in cache for 60 minutes after unused
    refetchOnMount: false, // Don't refetch on mount, use cached data
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: true, // Refetch on reconnect (user may have been granted/revoked permissions)
  })

  // Save to Zustand when data is loaded
  useEffect(() => {
    if (data && clientId) {
      setClientPermissions(clientId, data)
    }
  }, [data, clientId, setClientPermissions])

  // Use cached permissions from Zustand if available, otherwise use query data
  const effectivePermissions = clientPermissions || data

  const can = useCallback(
    (action: EPermission, entity: EResource): boolean => {
      if (!effectivePermissions || isLoading) return false
      const entityPermissions = effectivePermissions[entity]
      if (!entityPermissions) return false
      return entityPermissions[action] === true
    },
    [effectivePermissions, isLoading],
  )

  return {can, isLoading, error, permissions: effectivePermissions}
}
